import React from "react"

import { useTranslation } from "@bounce/i18n"
import { notEmpty } from "@bounce/util"

import { MapSection } from "./MapSection"
import type { CityScreenProps } from "../CityScreen"
import { FEATURES_POIS_COUNT } from "../constants"
import type { Interpolations } from "../hooks/useInterpolations"
import { ReviewsSlider } from "@/components/Generic/Reviews/ReviewsSlider/ReviewsSlider"
import { HowItWorksCompact } from "@/components/LuggageStorage/HowItWorks/HowItWorksCompact/HowItWorksCompact"
import { StoreGridSection } from "@/components/LuggageStorage/StoreSlider/StoreGridSection"
import StoreSliderSection from "@/components/LuggageStorage/StoreSlider/StoreSliderSection"
import { useStepsCompact } from "@/screens/LuggageStorage/CityScreen/hooks/useStepsCompact"

type MainContentProps = {
  interpolations: Interpolations
  searchUrl: string
  absolutePath: string
} & Pick<
  CityScreenProps,
  | "city"
  | "nearestMapStorePins"
  | "featuredPois"
  | "reviewsForSlider"
  | "mainCityStores"
  | "openAllTimeNearestStores"
  | "nearestCityLockers"
>

export const MainContent = ({
  city,
  nearestMapStorePins,
  featuredPois,
  reviewsForSlider,
  interpolations,
  searchUrl,
  absolutePath,
  mainCityStores,
  openAllTimeNearestStores,
  nearestCityLockers,
}: MainContentProps) => {
  const { t } = useTranslation("marketing-ls-city")
  const stepsCompact = useStepsCompact({ interpolations })

  const [featuredPoiOne, featuredPoiTwo] = featuredPois.slice(0, FEATURES_POIS_COUNT).filter(notEmpty)

  return (
    <>
      <MapSection
        city={city}
        className="mt-section"
        interpolations={interpolations}
        nearestStores={nearestMapStorePins}
        searchUrl={searchUrl}
      />
      <ReviewsSlider
        cityId={city.id}
        cityName={interpolations.CITY_NAME}
        className="pt-section"
        country={city.country}
        minimumReviewsCount={3}
        name="ReviewsSliderCity"
        reviews={reviewsForSlider}
        title={t("screen.cityScreen.reviewsSlider.titleV1", "Trusted bag storage in {CITY_NAME}", interpolations)}
      />
      {mainCityStores.length > 0 && (
        <StoreGridSection
          stores={mainCityStores}
          from="City"
          path={absolutePath}
          searchQuery={city.query}
          title={t(
            "screen.cityScreen.nearestCityStoresSlider.title",
            "Top-rated luggage storage in {{CITY_NAME}}",
            interpolations,
          )}
          subtitle={t(
            "screen.cityScreen.nearestCityStoresSlider.subtitleV2",
            "{{STORAGE_SPOT_COUNT}} secure storage spots in {{CITY_NAME}}. {{NUM_LOCATIONS}} all over the world.",
            interpolations,
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "CityNearestStores" }}
          renderStructuredData
        />
      )}
      {nearestCityLockers.length > 0 && (
        <StoreSliderSection
          name="NearestCityLockers"
          stores={nearestCityLockers}
          from="City"
          lowestPrice={interpolations.LOWEST_PRICE}
          path={absolutePath}
          searchQuery={city.query}
          subtitle={t(
            "screen.cityScreen.nearestCityLockersSlider.subtitleV1",
            "Find secure and convenient lockers around {{CITY_NAME}}.",
            interpolations,
          )}
          title={t(
            "screen.cityScreen.nearestCityLockersSlider.titleV2",
            "Store your bags in a locker in {{CITY_NAME}}",
            interpolations,
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "CityNearestLockers" }}
          renderStructuredData={false}
        />
      )}
      {openAllTimeNearestStores.length > 0 && (
        <StoreSliderSection
          name="OpenAllTimeNearestStores"
          stores={openAllTimeNearestStores}
          from="City"
          lowestPrice={interpolations.LOWEST_PRICE}
          path={absolutePath}
          searchQuery={city.query}
          title={t(
            "screen.cityScreen.openAllTimeNearestStoresSlider.titleOptimizedV2",
            "24/7 luggage storage in {{CITY_NAME}}",
            interpolations,
          )}
          subtitle={t(
            "screen.cityScreen.openAllTimeNearestStoresSlider.subtitleOptimizedV2",
            "Secure bag storage anytime you need it, including nights and weekends",
            interpolations,
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "CityOpenAllTimeNearestStores" }}
          renderStructuredData={false}
        />
      )}
      <HowItWorksCompact
        steps={stepsCompact}
        title={t(
          "screen.cityScreen.howItWorks.titleOptimized",
          "How to store your luggage in {{CITY_NAME}}",
          interpolations,
        )}
        className="pt-section"
      />
      {!!featuredPoiOne?.stores?.length && (
        <StoreSliderSection
          name="NearestStoresFeaturedPoiOne"
          stores={featuredPoiOne.stores}
          from="City"
          lowestPrice={interpolations.LOWEST_PRICE}
          path={absolutePath}
          searchQuery={featuredPoiOne.query}
          title={t(
            "screen.cityScreen.nearestStoresFeaturedPoiOneSlider.titleV3",
            "Looking for a luggage storage spot near {POI_NAME}?",
            {
              POI_NAME: featuredPoiOne.translatedName,
            },
          )}
          subtitle={t(
            "screen.cityScreen.nearestStoresFeaturedPoiOneSlider.subtitleV3",
            "Get affordable short term and long term luggage storage with one of our partners",
            {
              POI_NAME: featuredPoiOne.translatedName,
            },
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "FeaturedPoiOneNearestStores", poiName: featuredPoiOne.name }}
          renderStructuredData={false}
        />
      )}
      {!!featuredPoiTwo?.stores?.length && (
        <StoreSliderSection
          name="NearestStoresFeaturedPoiTwo"
          stores={featuredPoiTwo.stores}
          from="City"
          lowestPrice={interpolations.LOWEST_PRICE}
          path={absolutePath}
          searchQuery={featuredPoiTwo.query}
          title={t(
            "screen.cityScreen.nearestStoresFeaturedPoiTwoSlider.titleOptimized",
            "Left luggage near {{POI_NAME}}",
            {
              POI_NAME: featuredPoiTwo.translatedName,
            },
          )}
          subtitle={t(
            "screen.cityScreen.nearestStoresFeaturedPoiTwoSlider.subtitleV3",
            "Keep your bags safe while you explore {CITY_NAME}",
            interpolations,
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "FeaturedPoiTwoNearestStores", poiName: featuredPoiTwo.name }}
          renderStructuredData={false}
        />
      )}
    </>
  )
}
